import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import {
  useProjectUsersQuery,
  useRolesQuery,
  useAssignUserToProjectMutation,
  useUpdateProjectUserRoleMutation,
  useRemoveProjectUserMutation,
} from "@/hooks/queries/useProjectUsersQuery";
import { useAuth } from "@/contexts/AuthContext";
import { RoleType } from "@/types/project";
import type { ProjectUser } from "@/types/project";

interface ProjectUsersTabProps {
  projectId: number;
}

const ProjectUsersTab = ({ projectId }: ProjectUsersTabProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { user, isLoading: authLoading } = useAuth();

  const { data: projectUsers = [], isLoading, isError } = useProjectUsersQuery(projectId);
  const { data: roles = [] } = useRolesQuery();

  const currentUserId = Number(user?.id ?? 0);

  const isProjectOwner = projectUsers.some(
    (pu) => pu.userId === currentUserId && pu.roleType === RoleType.Owner
  );
  const canManage = isProjectOwner;
  const permissionsLoading = authLoading || isLoading;

  const assignMutation = useAssignUserToProjectMutation();
  const updateRoleMutation = useUpdateProjectUserRoleMutation();
  const removeMutation = useRemoveProjectUserMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [userToRemove, setUserToRemove] = useState<ProjectUser | null>(null);

  const getRoleLabel = (roleType: RoleType) => {
    switch (roleType) {
      case RoleType.Owner: return t("roleOwner");
      case RoleType.Editor: return t("roleEditor");
      case RoleType.Viewer: return t("projectViewer");
      default: return String(roleType);
    }
  };

  const getRoleBadgeVariant = (roleType: RoleType): "default" | "secondary" | "outline" => {
    switch (roleType) {
      case RoleType.Owner: return "default";
      case RoleType.Editor: return "secondary";
      case RoleType.Viewer: return "outline";
      default: return "outline";
    }
  };

  const filteredUsers = projectUsers.filter((pu) =>
    pu.userEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (pu.userNickName ?? "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddUser = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    assignMutation.mutate(
      { projectId, email: trimmed },
      {
        onSuccess: () => {
          setEmailInput("");
          setShowAddDialog(false);
        },
      }
    );
  };

  const handleRoleChange = (userId: number, newRoleId: string) => {
    updateRoleMutation.mutate({ projectId, userId, roleId: Number(newRoleId) });
  };

  const handleConfirmRemove = () => {
    if (!userToRemove) return;
    removeMutation.mutate(
      { projectId, userId: userToRemove.userId },
      { onSuccess: () => setUserToRemove(null) }
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {t("totalMembers")}: {projectUsers.length}
          </span>
        </div>
        {(canManage || permissionsLoading) && (
          <Button size="sm" onClick={() => setShowAddDialog(true)} disabled={permissionsLoading}>
            <Plus className="h-4 w-4 mr-1" />
            {t("addTeamMember")}
          </Button>
        )}
      </div>

      {/* Search */}
      <Input
        placeholder={t("searchTeamMembers")}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="max-w-sm"
      />

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="text-center py-12 text-destructive text-sm">
          Failed to load project members.
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {searchQuery ? t("noMembersFound") : t("noUsersAvailable")}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-40">{t("permission")}</TableHead>
                {(canManage || permissionsLoading) && <TableHead className="w-16" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((pu) => (
                <TableRow key={pu.id}>
                  <TableCell className="font-medium">
                    {pu.userNickName ?? (
                      <span className="text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{pu.userEmail}</TableCell>
                  <TableCell>
                    {canManage && roles.length > 0 ? (
                      <Select
                        value={String(pu.roleId)}
                        onValueChange={(val) => handleRoleChange(pu.userId, val)}
                        disabled={updateRoleMutation.isPending}
                      >
                        <SelectTrigger className="h-8 w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.id} value={String(role.id)}>
                              {getRoleLabel(role.roleType)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getRoleBadgeVariant(pu.roleType)}>
                        {getRoleLabel(pu.roleType)}
                      </Badge>
                    )}
                  </TableCell>
                  {(canManage || permissionsLoading) && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setUserToRemove(pu)}
                        title={t("removeMember")}
                        disabled={permissionsLoading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("addUserByEmail")}</DialogTitle>
            <DialogDescription>
              {t("addTeamMemberDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email"
              type="email"
              placeholder={t("enterUserEmail")}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddUser(); }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setEmailInput(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddUser}
              disabled={!emailInput.trim() || assignMutation.isPending}
            >
              {assignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("addTeamMember")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation */}
      <AlertDialog open={!!userToRemove} onOpenChange={(open) => { if (!open) setUserToRemove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmRemoveUser")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmRemoveUserDesc")}
              {userToRemove && (
                <span className="block mt-1 font-medium text-foreground">
                  {userToRemove.userEmail}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmRemove}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("removeMember")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectUsersTab;
